import { Fragment, JSX } from "react";
import { Icon } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { formatCurrency } from "@sellar-io/common";
import { OutOfStockProduct, PricingChangeProduct } from "./useValidateCheckout";
import { CheckoutOrderItem } from "./types";
import { CheckoutSelectedFulfillmentType } from "../../StockList";

export const CheckoutAlerts = ({
  alerts,
}: {
  alerts: Array<JSX.Element | false | undefined>;
}) => {
  const filteredAlerts = alerts.filter(Boolean) as JSX.Element[];

  if (filteredAlerts.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-2 bg-transparent p-2 empty:hidden lg:p-0">
      {filteredAlerts.map((alert, index) => (
        <Fragment key={index}>{alert}</Fragment>
      ))}
    </div>
  );
};

export const ErrorMessageAlert = ({
  message,
}: {
  message: string | undefined;
}) => {
  if (!message) return null;

  return (
    <Alert type="error" message={message} className="flex w-full flex-1" />
  );
};

const formatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

export const StockLevelAlert = ({
  outOfStockProducts,
  supplierName,
  onClick,
}: {
  outOfStockProducts: OutOfStockProduct[];
  supplierName: string;
  onClick: () => void;
}) => {
  const hasOutOfStockProducts = outOfStockProducts.length > 0;
  if (!hasOutOfStockProducts) return null;

  const listOfOutOfStockProducts = formatter.format(
    outOfStockProducts.map((outOfStockProduct) => {
      return [
        outOfStockProduct.name.trim(),
        outOfStockProduct.description.trim(),
      ].join(" • ");
    }),
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-1 items-center rounded-lg border border-orange-300 bg-orange-100 p-3"
    >
      <div className="flex flex-1 items-center justify-between gap-2">
        <Icon icon="CAUTION" size={0.8} color="#9a3412" />
        <p className="w-full text-start text-sm text-zinc-800">
          The following item(s) have low or no stock:{" "}
          <strong> {listOfOutOfStockProducts}.</strong> Return to {supplierName}
          's store to adjust your order and checkout again.
        </p>
      </div>
      <Icon icon="CHEVRON_RIGHT" color="text-orange-800" size={1} />
    </button>
  );
};

export const PricingChangeAlert = ({
  pricingChangeProducts,
  oldCheckoutOrderItems,
  newCheckoutOrderItems,
}: {
  pricingChangeProducts: PricingChangeProduct[];
  oldCheckoutOrderItems: CheckoutOrderItem[];
  newCheckoutOrderItems: CheckoutOrderItem[];
}) => {
  const hasPricingChangeProducts = pricingChangeProducts.length > 0;
  if (!hasPricingChangeProducts) return null;

  const listOfPricingChangeProducts = formatter.format(
    pricingChangeProducts.map((productWithPricingChange) => {
      return [
        productWithPricingChange.name.trim(),
        productWithPricingChange.description.trim(),
      ].join(" • ");
    }),
  );

  const oldOrderTotal = oldCheckoutOrderItems.reduce(
    (acc, curr) => acc + curr.price * curr.quantity,
    0,
  );

  const newOrderTotal = newCheckoutOrderItems.reduce(
    (acc, curr) => acc + curr.price * curr.quantity,
    0,
  );

  if (oldOrderTotal === newOrderTotal) return null;

  return (
    <div className="flex flex-1 items-center rounded-lg border border-blue-300 bg-blue-100 p-3">
      <div className="flex flex-1 items-center justify-between gap-2">
        <Icon icon="INFORMATION" size={0.8} color="#1E40AF" />
        <p className="w-full text-start text-sm text-zinc-800">
          The following item(s) price has changed:{" "}
          <strong> {listOfPricingChangeProducts}.</strong> Your subtotal has
          changed from {formatCurrency(oldOrderTotal)} to{" "}
          {formatCurrency(newOrderTotal)}. See 'Your order' for more details
        </p>
      </div>
    </div>
  );
};

export const DistributionEducationAlert = ({
  supplierName,
  fulfillmentType,
}: {
  supplierName: string;
  fulfillmentType: CheckoutSelectedFulfillmentType;
}) => {
  if (fulfillmentType !== "sellarDistribution") {
    return null;
  }

  return (
    <div className="flex gap-2 rounded-2xl border border-[#E8D3F4] bg-[#E8D3F426] p-4">
      <div className="hidden h-14 min-h-14 w-14 min-w-14 flex-col items-center justify-center rounded-full bg-[#E8D3F4]/20 sm:flex">
        <div className="flex h-10 min-h-10 w-10 min-w-10 items-center justify-center rounded-full bg-[#E8D3F4]">
          <Icon
            icon="SELLAR_DISTRIBUTION_TRUCK"
            size={1}
            viewBox="0 0 16 16"
            className="text-[#8C23C7]"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col">
          <p className="font-semibold">
            Your order will be delivered by Sellar
          </p>
          <p className="text-sm text-zinc-600">
            {supplierName} offer our free expert dray delivery service, so you
            get
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="flex items-center gap-0.5 text-sm font-semibold text-[#8C23C7]">
            <Icon icon="CHECK" size={0.7} className="text-[#BA7BDD]" /> Free
            delivery
          </div>
          <div className="flex items-center gap-0.5 text-sm font-semibold text-[#8C23C7]">
            <Icon icon="CHECK" size={0.7} className="text-[#BA7BDD]" /> Flexible
            delivery times
          </div>
          <div className="flex items-center gap-0.5 text-sm font-semibold text-[#8C23C7]">
            <Icon icon="CHECK" size={0.7} className="text-[#BA7BDD]" /> Combined
            with other suppliers
          </div>
        </div>
      </div>
    </div>
  );
};
